import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import MarketingTextSection from "../MarketingText/MarketingText";
import SelectBox from "../../BaseComponents/SelectBox/SelectBox";
import { useToast } from "../../../context/ToastContext";
import { useGetRegions } from "../../../api/geo/useRegions";
import {
  useGetHolidayTypes,
  getCityHolidayTypes,
} from "../../../api/geo/useHolidayTypes";
import GeoImageUpload from "../GeoImageUpload/GeoImageUpload";
import styles from "./AddEditCityModal.module.css";

const emptyForm = {
  name: "",
  code: "",
  countryId: "",
  regionId: "",
  active: true,
  mainImage: "",
  holidayTypeIds: [],
  marketing: {
    title: "",
    para1: "",
    para2: ""
  }
};

const AddEditCityModal = ({
  open,
  onClose,
  mode = "add",
  initialData,
  countries = [],
  onSave,
  saveLoading = false
}) => {
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [submitError, setSubmitError] = useState(null);

  // Regions filtered by the selected country (region must belong to the country)
  const { data: regionsData = [] } = useGetRegions({
    page: 1,
    limit: 500,
    countryId: form.countryId || undefined,
    sortBy: "name",
    sortOrder: "ASC",
  });
  const regionOptions = Array.isArray(regionsData) ? regionsData : [];

  const { data: allHolidayTypes = [] } = useGetHolidayTypes({
    page: 1,
    limit: 500,
    languageCode: "ENG",
  });
  const holidayTypeOptions = Array.isArray(allHolidayTypes) ? allHolidayTypes : [];

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);

    if (mode === "edit" && initialData) {
      setForm({
        name: initialData.name || "",
        code: initialData.code || initialData.isoCode || "",
        countryId: initialData.countryId ?? "",
        regionId: initialData.regionId ?? "",
        active: initialData.active ?? true,
        mainImage: initialData.mainImage || "",
        holidayTypeIds: [],
        marketing: {
          title: initialData.marketing?.title ?? initialData.title ?? "",
          para1: initialData.marketing?.para1 ?? initialData.paragraph1 ?? "",
          para2: initialData.marketing?.para2 ?? initialData.paragraph2 ?? ""
        }
      });
      // Preload assigned holiday types for this city
      if (initialData.id) {
        getCityHolidayTypes(initialData.id, { languageCode: "ENG" })
          .then((res) => {
            if (res?.success && Array.isArray(res.data)) {
              setForm((prev) => ({ ...prev, holidayTypeIds: res.data.map((t) => t.id) }));
            }
          })
          .catch(() => {});
      }
    }

    if (mode === "add") {
      setForm(emptyForm);
    }
  }, [open, mode, initialData]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // Changing the country clears the region (region must belong to the country)
  const handleCountryChange = (value) => {
    setForm(prev => ({ ...prev, countryId: value, regionId: "" }));
  };

  const handleMarketingChange = marketing => {
    setForm(prev => ({ ...prev, marketing }));
  };

  const toggleHolidayType = (typeId) => {
    setForm((prev) => {
      const current = prev.holidayTypeIds || [];
      const next = current.includes(typeId)
        ? current.filter((id) => id !== typeId)
        : [...current, typeId];
      return { ...prev, holidayTypeIds: next };
    });
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      showToast("City name is required", "error");
      return;
    }
    if (!form.code?.trim()) {
      showToast("City code is required", "error");
      return;
    }
    if (!form.countryId) {
      showToast("Country is required", "error");
      return;
    }
    setSubmitError(null);
    const payload = {
      code: form.code.trim().toUpperCase(),
      isoCode: form.code.trim().toUpperCase(),
      countryId: Number(form.countryId),
      regionId: form.regionId ? Number(form.regionId) : null,
      active: form.active,
      mainImage: form.mainImage || null,
      name: form.name.trim(),
      holidayTypeIds: form.holidayTypeIds || [],
      title: (form.marketing?.title || "").trim(),
      paragraph1: (form.marketing?.para1 || "").trim(),
      paragraph2: (form.marketing?.para2 || "").trim(),
      description: (form.marketing?.para1 || "").trim()
    };
    try {
      await onSave(payload);
      onClose();
    } catch (err) {
      setSubmitError(err.message || "Failed to save city");
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={mode === "add" ? "Add City" : "Edit City"}
      width="680px"
      footer={
        <>
          {submitError && (
            <span className={styles.submitError}>{submitError}</span>
          )}
          <button
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={saveLoading}
          >
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <span className={styles.spinner} aria-hidden />
                Saving…
              </>
            ) : mode === "add" ? (
              "+ Create City"
            ) : (
              "Save Changes"
            )}
          </button>
        </>
      }
    >
      <div className={styles.formGrid}>
        <div className={styles.field}>
          <label>City Name</label>
          <input
            value={form.name}
            onChange={e => handleChange("name", e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label>City Code</label>
          <input
            value={form.code}
            onChange={e =>
              handleChange("code", e.target.value.toUpperCase())
            }
          />
        </div>

        <div className={styles.fieldWide}>
          <label>Country</label>
          <SelectBox
            placeholder="Select country"
            value={String(form.countryId)}
            options={countries.map(c => ({
              label: c.name,
              value: String(c.id)
            }))}
            onChange={e => handleCountryChange(e.target.value)}
          />
        </div>

        <div className={styles.fieldWide}>
          <label>Region</label>
          <SelectBox
            placeholder={form.countryId ? "Select region (optional)" : "Select a country first"}
            disabled={!form.countryId}
            value={form.regionId ? String(form.regionId) : ""}
            options={[
              { label: "— No region —", value: "" },
              ...regionOptions.map(r => ({
                label: r.name,
                value: String(r.id)
              }))
            ]}
            onChange={e => handleChange("regionId", e.target.value)}
          />
        </div>

        <div className={styles.statusRow}>
          <label>{form.active ? "Active City" : "Inactive City"}</label>
          <ToggleSwitch
            checked={form.active}
            onChange={() =>
              handleChange("active", !form.active)
            }
          />
        </div>
      </div>

      <div className={styles.field}>
        <label>Main Image</label>
        <GeoImageUpload value={form.mainImage} onChange={(url) => handleChange("mainImage", url)} />
      </div>

      <div className={styles.holidayTypesSection}>
        <div className={styles.holidayTypesHeader}>
          <span className={styles.holidayTypesLabel}>Holiday types applied to this city</span>
          {(form.holidayTypeIds || []).length > 0 && (
            <span className={styles.selectedBadge}>{(form.holidayTypeIds || []).length} selected</span>
          )}
        </div>
        {holidayTypeOptions.length === 0 ? (
          <div className={styles.noTypesHint}>No holiday types available. Add types first.</div>
        ) : (
          <div className={styles.holidayTypesGrid}>
            {holidayTypeOptions.map((type) => (
              <label
                key={type.id}
                className={`${styles.holidayTypeItem} ${(form.holidayTypeIds || []).includes(type.id) ? styles.holidayTypeItemSelected : ""}`}
              >
                <input
                  type="checkbox"
                  checked={(form.holidayTypeIds || []).includes(type.id)}
                  onChange={() => toggleHolidayType(type.id)}
                />
                <span className={styles.holidayTypeIcon}>{type.icon || "•"}</span>
                <span>{type.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <MarketingTextSection
        value={form.marketing}
        onChange={handleMarketingChange}
      />
    </BaseModal>
  );
};

export default AddEditCityModal;
